import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import UserEditSubmitButton from 'components/UserEditSubmitButton/UserEditSubmitButton';
import { usePerms } from 'contexts/PermsContext';
import { useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { getUserProfileInfo, updateUserProfile } from 'services/user/user.service';
import { ClassicUpdatePassword as UpdatePassword } from './ClassicUpdatePassword';
const isDev = process.env.NODE_ENV === 'development';

/**
 * The classic Account tab — the page as it was before the Merlin Slate restyle (0c1fb517e).
 * Shown when the person picks Classic navigation in the display settings.
 */
export const ClassicUserInfo = () => {
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [originalUserInfo, setOriginalUserInfo] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Permissions context
  const perms = usePerms();

  // Define required permission for viewing/updating user info
  const canViewUserInfo = perms?.testPermission
    ? perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewUsers'])
    : false;

  const [inputUserInfo, setInputUserInfo] = useState({
    name: '',
    email: '',
    employeeId: '',
    phoneNumber: '',
    dob: '',
    gender: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipcode: '',
    },
    emergencyContact: {
      name: '',
      phone: '',
    },
  });

  const defaultAddress = {
    street: '',
    city: '',
    state: '',
    zipcode: '',
  };

  const defaultEmergencyContact = {
    name: '',
    phone: '',
  };

  const sanitizeString = (value) => (value === undefined || value === null ? '' : String(value));

  const normalizeDateForInput = (value) => {
    if (!value) return '';

    // Accept already-correct values
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

    // Handle MM/DD/YYYY or MM-DD-YYYY
    const match = value.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/);
    if (match) {
      let [, month, day, year] = match;
      if (year.length === 2) year = `20${year}`;
      const y = year.padStart(4, '0');
      const m = month.padStart(2, '0');
      const d = day.padStart(2, '0');
      return `${y}-${m}-${d}`;
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);

    return '';
  };

  const normalizeCompositeField = (value, fallback) => {
    const mergeFallback = (obj) => ({ ...fallback, ...(obj || {}) });

    if (!value) return mergeFallback();

    const parseJsonString = (input) => {
      if (!input) return null;
      try {
        const parsed = JSON.parse(input);
        if (typeof parsed === 'string') return parseJsonString(parsed);
        return parsed;
      } catch {
        if (isDev) console.warn('Failed to parse composite field:', input);
        return null;
      }
    };

    const buildStringFromNumericKeys = (obj) => {
      const numericKeys = Object.keys(obj).filter((key) => /^\d+$/.test(key));
      if (!numericKeys.length) return null;
      return numericKeys
        .sort((a, b) => Number(a) - Number(b))
        .map((key) => obj[key])
        .join('');
    };

    if (typeof value === 'string' || value instanceof String) {
      const parsed = parseJsonString(value.toString());
      return mergeFallback(parsed);
    }

    if (typeof value === 'object') {
      const serialized = buildStringFromNumericKeys(value);
      if (serialized) {
        const parsed = parseJsonString(serialized);
        return mergeFallback(parsed);
      }
      return mergeFallback(value);
    }

    return mergeFallback();
  };

  const formatEmergencyContactForDisplay = (contact = {}) => ({
    name: sanitizeString(contact?.name),
    phone: formatPhoneNumberForDisplay(sanitizeString(contact?.phone || '')),
  });

  const formatEmergencyContactForApi = (contact = {}) => ({
    ...contact,
    phone: formatPhoneNumberForApi(contact?.phone || ''),
  });

  const buildProfileFromResponse = (data = {}) => ({
    name: sanitizeString(data?.name),
    email: sanitizeString(data?.email),
    employeeId: sanitizeString(data?.employeeId),
    phoneNumber: formatPhoneNumberForDisplay(sanitizeString(data?.phoneNumber || '')),
    dob: normalizeDateForInput(data?.dob),
    gender: sanitizeString(data?.gender) || 'Not Specified',
    address: normalizeCompositeField(data?.address, defaultAddress),
    emergencyContact: formatEmergencyContactForDisplay(
      normalizeCompositeField(data?.emergencyContact, defaultEmergencyContact),
    ),
  });

  const cloneProfile = (profile) => JSON.parse(JSON.stringify(profile || {}));

  const profilesMatch = (a, b) => {
    if (!a || !b) return false;
    return JSON.stringify(a) === JSON.stringify(b);
  };

  const updateUserInfoState = (updater) => {
    setInputUserInfo((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      if (originalUserInfo) setHasChanges(!profilesMatch(next, originalUserInfo));
      else setHasChanges(true);
      return next;
    });
  };

  const formatDateForApi = (value) => {
    if (!value) return null;
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) return value;
    const match = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (match) {
      const [, year, month, day] = match;
      return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
    }
    return value;
  };

  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);
      setFetchError(null);
      try {
        if (!canViewUserInfo) {
          setFetchError('You do not have permission to view this information.');
          setIsLoading(false);
          return;
        }
        const res = await getUserProfileInfo();
        const profile = buildProfileFromResponse(res?.data?.result);
        setInputUserInfo(profile);
        setOriginalUserInfo(cloneProfile(profile));
        setHasChanges(false);
      } catch (err) {
        if (isDev) console.error('Failed to load user profile info', err);
        setFetchError('Failed to load account information. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [canViewUserInfo]);
  const handleSubmit = async () => {
    if (isLoading || isSaving || !hasChanges) return;

    if (!canViewUserInfo) {
      setError('You do not have permission to update this information.');
      return;
    }

    const payload = {
      phoneNumber: formatPhoneNumberForApi(inputUserInfo.phoneNumber || ''),
      gender: inputUserInfo.gender,
      birthday: formatDateForApi(inputUserInfo.dob),
      address: JSON.stringify(normalizeCompositeField(inputUserInfo.address, defaultAddress)),
      emergencyContact: JSON.stringify(
        formatEmergencyContactForApi(
          normalizeCompositeField(inputUserInfo.emergencyContact, defaultEmergencyContact),
        ),
      ),
    };

    setIsSaving(true);
    setError(null);
    setMessage(null);

    try {
      const res = await updateUserProfile(
        payload.phoneNumber,
        payload.gender,
        payload.birthday,
        payload.address,
        payload.emergencyContact,
      );

      if (res?.status === 200) {
        setMessage('Account information updated successfully');
        setError(null);
        setOriginalUserInfo(cloneProfile(inputUserInfo));
        setHasChanges(false);
      } else {
        const errMsg = res?.response?.data?.message || 'Unknown error occurred while saving';
        setError('Error updating user info: ' + errMsg);
        setMessage(null);
      }
    } catch (e) {
      const errMsg = e?.response?.data?.message || e?.message || 'Unknown error';
      setError('Error updating user info: ' + errMsg);
      setMessage(null);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading && !originalUserInfo) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column justify-content-between w-100 h-100">
      <div>
        {!showChangePassword && (
          <Form>
            <div className="fw-bold fs-4">Information</div>
            <div className="row g-5" style={{ marginTop: 20 }}>
              <Form.Group className="col-12 col-lg-4">
                <InputItem
                  editable={false}
                  title="Name"
                  value={inputUserInfo.name || ''}
                  setValue={() => {}}
                />
              </Form.Group>
              <Form.Group className="col-12 col-lg-4">
                <InputItem
                  editable={false}
                  title="Email"
                  value={inputUserInfo.email || ''}
                  setValue={() => {}}
                  stylesOverride={{ backgroundColor: 'var(--color-primary-shadow)' }}
                />
              </Form.Group>
              <Form.Group className="col-12 col-lg-4">
                <InputItem
                  editable={false}
                  title="Employee ID"
                  value={inputUserInfo.employeeId || ''}
                  setValue={() => {}}
                />
              </Form.Group>
            </div>
            <div className="row g-5" style={{ marginTop: 20 }}>
              <Form.Group className="col-12 col-md-4">
                <InputItem
                  editable={!isLoading}
                  title="Phone Number"
                  type="tel"
                  value={inputUserInfo.phoneNumber || ''}
                  setValue={(value) => {
                    const formatted = formatPhoneNumberForDisplay(value);
                    updateUserInfoState((prev) => ({
                      ...prev,
                      phoneNumber: formatted,
                    }));
                  }}
                />
              </Form.Group>
              <Form.Group className="col-12 col-md-4">
                <InputItem
                  editable={!isLoading}
                  title="Birthday"
                  type="date"
                  value={inputUserInfo.dob || ''}
                  setValue={(value) => {
                    updateUserInfoState((prev) => ({
                      ...prev,
                      dob: sanitizeString(value),
                    }));
                  }}
                />
              </Form.Group>
              <Form.Group className="col-12 col-md-4">
                <InputItem
                  editable={!isLoading}
                  title="Gender"
                  inputType="select"
                  options={['Female', 'Male', 'Not Specified']}
                  value={inputUserInfo.gender || 'Not Specified'}
                  setValue={(value) => {
                    updateUserInfoState((prev) => ({
                      ...prev,
                      gender: sanitizeString(value),
                    }));
                  }}
                />
              </Form.Group>
            </div>
            <div className="fw-bold fs-4" style={{ marginTop: 40 }}>
              Home Address
            </div>
            <div className="row g-5" style={{ marginTop: 20 }}>
              <Form.Group className="col-12 col-md-3">
                <InputItem
                  editable={!isLoading}
                  title="Street"
                  value={inputUserInfo.address?.street || ''}
                  setValue={(value) => {
                    updateUserInfoState((prev) => ({
                      ...prev,
                      address: {
                        ...prev.address,
                        street: sanitizeString(value),
                      },
                    }));
                  }}
                />
              </Form.Group>
              <Form.Group className="col-12 col-md-3">
                <InputItem
                  editable={!isLoading}
                  title="City / County"
                  value={inputUserInfo.address?.city || ''}
                  setValue={(value) => {
                    updateUserInfoState((prev) => ({
                      ...prev,
                      address: {
                        ...prev.address,
                        city: sanitizeString(value),
                      },
                    }));
                  }}
                />
              </Form.Group>
              <Form.Group className="col-12 col-md-3">
                <InputItem
                  editable={!isLoading}
                  title="State"
                  value={inputUserInfo.address?.state || ''}
                  setValue={(value) => {
                    updateUserInfoState((prev) => ({
                      ...prev,
                      address: {
                        ...prev.address,
                        state: sanitizeString(value),
                      },
                    }));
                  }}
                />
              </Form.Group>
              <Form.Group className="col-12 col-md-3">
                <InputItem
                  editable={!isLoading}
                  title="Zipcode"
                  value={inputUserInfo.address?.zipcode || ''}
                  setValue={(value) => {
                    updateUserInfoState((prev) => ({
                      ...prev,
                      address: {
                        ...prev.address,
                        zipcode: sanitizeString(value),
                      },
                    }));
                  }}
                />
              </Form.Group>
            </div>
            <div className="fw-bold fs-4" style={{ marginTop: 40 }}>
              Emergency Contact
            </div>
            <div className="row g-5" style={{ paddingBottom: 10 }}>
              <Form.Group className="col-12 col-md-4">
                <InputItem
                  editable={!isLoading}
                  title="Name"
                  value={inputUserInfo.emergencyContact?.name || ''}
                  setValue={(value) => {
                    updateUserInfoState((prev) => ({
                      ...prev,
                      emergencyContact: {
                        ...prev.emergencyContact,
                        name: sanitizeString(value),
                      },
                    }));
                  }}
                />
              </Form.Group>
              <Form.Group className="col-12 col-md-4">
                <InputItem
                  editable={!isLoading}
                  title="Phone"
                  type="tel"
                  value={inputUserInfo.emergencyContact?.phone || ''}
                  setValue={(value) => {
                    const formatted = formatPhoneNumberForDisplay(value);
                    updateUserInfoState((prev) => ({
                      ...prev,
                      emergencyContact: {
                        ...prev.emergencyContact,
                        phone: formatted,
                      },
                    }));
                  }}
                />
              </Form.Group>
            </div>
          </Form>
        )}
      </div>
      <div>
        {showChangePassword && <UpdatePassword setShowChangePassword={setShowChangePassword} />}
        {!showChangePassword && (
          <div
            className="d-flex align-items-center justify-content-between flex-column flex-sm-row gap-3"
            style={{ marginTop: 50 }}
          >
            <Button
              onClick={() => setShowChangePassword(true)}
              className="p-2"
              style={{ borderRadius: 20 }}
              variant="secondary"
            >
              Change Password
            </Button>
            <div className="d-flex flex-column justify-content-center text-center">
              {fetchError && <Form.Text className="text-danger">{fetchError}</Form.Text>}
              {error && <Form.Text className="text-danger">{error}</Form.Text>}
              {message && <Form.Text>{message}</Form.Text>}
            </div>
            <UserEditSubmitButton
              title="Save"
              disabled={isLoading || isSaving || !hasChanges}
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const InputItem = ({
  editable,
  title,
  value = '',
  setValue,
  type = 'text',
  inputType = 'input',
  options,
  stylesOverride = {},
}) => {
  const selectOptions = (options ?? []).map((option) => ({ value: option, label: option }));

  return (
    <div className="d-flex flex-column w-100">
      <div>{title}</div>
      {inputType === 'input' && (
        <Form.Control
          className="p-1 mt-1 rounded-2"
          style={{
            ...stylesOverride.input,
            background: editable ? 'var(--color-primary-shadow)' : 'var(--bs-secondary)',
          }}
          value={value}
          type={type}
          disabled={!editable}
          onChange={(e) => setValue(e.target.value)}
        ></Form.Control>
      )}
      {inputType === 'select' && (
        <BaseSelect
          wrapperClassName="mt-1"
          isSearchable
          // The read-only look is a meaningful signal, not decoration.
          styles={{
            ...stylesOverride.select,
            control: (base) => ({
              ...base,
              background: editable ? 'var(--color-primary)' : 'var(--bs-secondary-color)',
              color: editable ? 'var(--bs-body-color)' : 'var(--bs-secondary-color)',
            }),
          }}
          selectProps={{
            'aria-label': title,
            options: selectOptions,
            value: findSelectOption(selectOptions, value),
            isDisabled: !editable,
            onChange: (option) => setValue(option?.value ?? ''),
          }}
        />
      )}
    </div>
  );
};
const formatPhoneNumberForDisplay = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const formatPhoneNumberForApi = (value) => value.replace(/\D/g, '').slice(0, 10);
